/**
 * TaskBoard — "By stage" kanban view for the Tasks page.
 *
 * Columns are generated from a single workflow template's ordered stages
 * (plus a final "Completed" column). Dragging a card one column forward
 * completes its current stage (backend validates required fields and
 * auto-advances). Other moves are rejected — open the task detail for
 * non-sequential stage changes.
 */

import React from 'react';
import { Box, Paper, Typography, Chip, Tooltip } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { BarChart3 } from 'lucide-react';

const PRIORITY_COLORS = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  critical: 'error',
};

const parseStage = (cs) => {
  if (!cs) return null;
  return typeof cs === 'string' ? JSON.parse(cs) : cs;
};

const TaskCard = ({ task, provided, snapshot, onClick }) => {
  const isOverdue = task.status === 'active' && task.due_date && new Date(task.due_date) < new Date();
  return (
    <Paper
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      onClick={() => onClick(task)}
      elevation={snapshot.isDragging ? 6 : 1}
      sx={{
        p: 1.25,
        mb: 1,
        cursor: 'pointer',
        borderLeft: '3px solid',
        borderLeftColor: `${PRIORITY_COLORS[task.priority] === 'default' ? 'grey.400' : `${PRIORITY_COLORS[task.priority]}.main`}`,
        '&:hover': { bgcolor: 'action.hover' },
        ...provided.draggableProps.style,
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
        {task.title}
      </Typography>
      {task.site_name && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {task.site_name}{task.sub_site_name ? ` • ${task.sub_site_name}` : ''}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Chip label={task.priority} size="small" color={PRIORITY_COLORS[task.priority] || 'default'} sx={{ height: 18, fontSize: 11, textTransform: 'capitalize' }} />
        {task.due_date && (
          <Typography variant="caption" color={isOverdue ? 'error' : 'text.secondary'}>
            {new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
          </Typography>
        )}
        {task.assigned_to_name && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {task.assigned_to_name}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const TaskBoard = ({ tasks, stages, canUpdate, onAdvance, onTaskClick, onInvalidMove }) => {
  // Ordered stage columns from the workflow template
  const stageCols = stages.map(s => ({
    key: `stage:${s.name}`,
    label: s.name,
    stageType: s.stage_type,
  }));
  const stageKeys = stageCols.map(c => c.key);

  // Group tasks into columns by their current stage name
  const grouped = {};
  stageKeys.forEach(k => { grouped[k] = []; });
  grouped.other = [];
  grouped.completed = [];

  tasks
    .filter(t => t.status !== 'cancelled')
    .forEach(t => {
      const cs = parseStage(t.current_stage);
      if (t.status === 'completed' || !cs) {
        grouped.completed.push(t);
        return;
      }
      const key = `stage:${cs.name}`;
      if (grouped[key]) grouped[key].push(t);
      else grouped.other.push(t); // stage name no longer in template (snapshot of older version)
    });

  const allColumns = [
    ...stageCols,
    ...(grouped.other.length ? [{ key: 'other', label: 'Other stages' }] : []),
    { key: 'completed', label: 'Completed' },
  ];

  // Valid drag: one stage forward, or last stage → Completed
  const isValidMove = (fromKey, toKey) => {
    const fromIdx = stageKeys.indexOf(fromKey);
    if (fromIdx === -1) return false;
    if (fromIdx === stageKeys.length - 1) return toKey === 'completed';
    return toKey === stageKeys[fromIdx + 1];
  };

  const handleDragEnd = ({ source, destination, draggableId }) => {
    if (!destination || destination.droppableId === source.droppableId) return;
    if (!isValidMove(source.droppableId, destination.droppableId)) {
      onInvalidMove?.('Cards can only move forward one stage at a time. Open the task to make other changes.');
      return;
    }
    const task = tasks.find(t => String(t.id) === String(draggableId));
    if (task) onAdvance(task);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, alignItems: 'flex-start' }}>
        {allColumns.map(col => (
          <Paper
            key={col.key}
            variant="outlined"
            sx={{ minWidth: 272, maxWidth: 272, flexShrink: 0, bgcolor: 'grey.50', borderRadius: 2 }}
          >
            <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
              {col.stageType === 'monitoring' && (
                <Tooltip title="Monitoring & Impact Assessment stage">
                  <BarChart3 size={14} />
                </Tooltip>
              )}
              <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flexGrow: 1 }}>
                {col.label}
              </Typography>
              <Chip label={grouped[col.key].length} size="small" sx={{ height: 18, fontSize: 11 }} />
            </Box>
            <Droppable droppableId={col.key}>
              {(provided, snapshot) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    p: 1,
                    minHeight: 140,
                    maxHeight: 'calc(100vh - 320px)',
                    overflowY: 'auto',
                    bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                    transition: 'background-color 120ms ease',
                  }}
                >
                  {grouped[col.key].map((task, index) => (
                    <Draggable
                      key={task.id}
                      draggableId={String(task.id)}
                      index={index}
                      isDragDisabled={!canUpdate || col.key === 'completed' || col.key === 'other'}
                    >
                      {(prov, snap) => (
                        <TaskCard task={task} provided={prov} snapshot={snap} onClick={onTaskClick} />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {grouped[col.key].length === 0 && !snapshot.isDraggingOver && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
                      No tasks
                    </Typography>
                  )}
                </Box>
              )}
            </Droppable>
          </Paper>
        ))}
      </Box>
    </DragDropContext>
  );
};

export default TaskBoard;
